import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import ListView from "views/common/list";
import React from "react";
import Button from "@edaz/edazkit/Button/index";
import PaymentBankTransferViewModel from "viewModels/finance/payments/bankTransfer/paymentBankTransferViewModel";
import type TransferPaymentResponse from "entities/transferPaymentResponse";
import PaymentBankTransferFilters from "./filters/bankTransferFilters";
import { formatAmount, formatDate } from "utils/localizations";
import { emptyValue } from "utils/emptyValue";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";
import Tooltip from "@edaz/edazkit/Tooltip";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import CounterpartyBankAccountType from "entities/counterpartyBankAccountType";

// eslint-disable-next-line sonarjs/cognitive-complexity
const PaymentBankTransferView: ViewComponent<PaymentBankTransferViewModel> = observer(({ vm }) => {
  const headerClassName = "py-4 align-middle";
  const wrapClassName = "text-wrap";

  const onItemClick = vm.onItemClick();
  const tooltip = (value: string, property: string, maxSize: string) =>
    value ? (
      <>
        <Tooltip placement="top" id={`${property}-tooltip`} className={`${property}-tooltip-class`} tooltip={value}>
          <span
            style={{
              textDecorationLine: "underline",
              textDecorationStyle: "dotted",
              textDecorationColor: "rgb(14, 112, 240)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              maxWidth: `${maxSize}px`,
            }}>
            {value}
          </span>
        </Tooltip>
      </>
    ) : (
      emptyValue
    );

  const columns: ColumnDefinition<TransferPaymentResponse>[] = [
    {
      property: "statementDate",
      title: vm.translateAttribute("statementDate"),
      headerClassName: `${headerClassName} ${wrapClassName}`,
      cellClassName: wrapClassName,
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "bankTransactionType",
      headerClassName: `${headerClassName}`,
      cellClassName: wrapClassName,
      cellProps: _ => ({ style: { minWidth: 150 } }),
      title: vm.translateAttribute("bankTransactionType"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "amount",
      headerClassName: `${headerClassName}`,
      title: vm.translateAttribute("amount"),
      cellProps: _ => ({ style: { color: _.value > 0 ? "" : "#c3113c" } }),
      cellClassName: `${wrapClassName} text-right`,
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "paymentTemplateId",
      headerClassName: `${headerClassName} ${wrapClassName} text-left`,
      title: vm.translateAttribute("paymentTemplateId"),
      cellClassName: wrapClassName,
      cellProps: _ => ({ style: { minWidth: 300 } }),
      valueFormatter: props => (props.value ? vm.translatePaymentTemplate(props.value) : emptyValue),
    },
    {
      property: "counterpartyAccountNumber",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("counterpartyAccountNumber"),
      valueFormatter: ({ item }) =>
        item.counterpartyAccountNumber
          ? item.counterpartyAccountType === CounterpartyBankAccountType.Domestic
            ? formatBankAccount({ accountNumber: item.counterpartyAccountNumber })
            : item.counterpartyAccountNumber
          : emptyValue,
    },
    {
      property: "counterpartyBank",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("counterpartyBank"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "counterpartyName",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("counterpartyName"),
      cellClassName: wrapClassName,
      cellProps: _ => ({ style: { minWidth: 125 } }),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "variableSymbol",
      headerClassName: `${headerClassName} text-right`,
      title: vm.translateAttribute("variableSymbol"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "newVariableSymbol",
      headerClassName: `${headerClassName}`,
      title: (
        <FormLabelWithTooltip withoutFormLabel tooltipText={vm.translate("newVariableSymbolTooltip")}>
          {vm.translateAttribute("newVariableSymbol")}
        </FormLabelWithTooltip>
      ),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "constantSymbol",
      headerClassName: `${headerClassName} text-right`,
      title: vm.translateAttribute("constantSymbol"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "specificSymbol",
      headerClassName: `${headerClassName} text-right`,
      title: vm.translateAttribute("specificSymbol"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "messageForRecipient",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("messageForRecipient"),
      cellProps: _ => ({ style: { minWidth: 100 } }),
      cellClassName: `${wrapClassName}`,
      valueFormatter: props => tooltip(props.value, "messageForRecipient", "100"),
    },
    {
      property: "additionalInformation",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("additionalInformation"),
      cellProps: _ => ({ style: { minWidth: 100 } }),
      cellClassName: `${wrapClassName}`,
      valueFormatter: props => tooltip(props.value, "additionalInformation", "100"),
    },
    {
      property: "bankTransactionId",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("bankTransactionId"),
      valueFormatter: props => props.value ?? emptyValue,
    },
  ];

  let actionButtons;
  if (onItemClick) {
    actionButtons = (item: TransferPaymentResponse) => (
      <Button size="sm" className="rounded btn btn-success btn-sm" onClick={() => onItemClick(item)}>
        {vm.translate("detail")}
      </Button>
    );
  }
  return (
    <>
      {!vm.activeChild && <PaymentBankTransferFilters vm={vm} />}
      <ListView
        showDetailWithList={true}
        vm={vm}
        columns={columns}
        hideTitle={true}
        mandatoryFilter={true}
        actionButtons={actionButtons}
      />
    </>
  );
});

registerView(PaymentBankTransferView, PaymentBankTransferViewModel);
